import React from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  ButtonBase,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useQuery } from "@apollo/client";
import { PRODUCTS_LIST_QUERY } from "../graphql/products";
import { buildImageUrl } from "../config";
import { useNavigate } from "react-router-dom";

type BackendProduct = {
  id: string;
  name: string;
  slug?: string | null;
  customSchema?: string | null;
  altText?: string | null;
  productType: string;
  shortDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

type ProductsListData = {
  publicProducts: BackendProduct[];
  publicProductsCount: number;
};

type Product = {
  id: string;
  name: string;
  productType: string;
  shortDescription?: string | null;
  price: number;
};

interface YouMayAlsoLikeProps {
  product: Product;
}

const YouMayAlsoLike: React.FC<YouMayAlsoLikeProps> = ({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const formatPrice = (value: number) =>
    `${value.toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

  const { data, loading, error } = useQuery<ProductsListData>(
    PRODUCTS_LIST_QUERY,
    {
      variables: {
        productType: product.productType,
        offset: 0,
        limit: 12,
        order: null,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const suggestions: BackendProduct[] = (data?.publicProducts ?? []).filter(
    (p) => p.id !== product.id,
  );

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
      {suggestions.length > 0 && (
        <Typography
          component="h2"
          sx={{
            fontFamily: `"Bodoni Moda", serif`,
            fontSize: { xs: "1.5rem", md: "2rem" },
            fontWeight: 500,
            textAlign: "center",
            mb: { xs: 3, md: 5 },
          }}
        >
          You May Also Like
        </Typography>
      )}

      {loading && !data && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && !suggestions.length && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
            Couldn’t load recommendations right now.
          </Typography>
        </Box>
      )}

      {suggestions.length > 0 && (
        <>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={20}
            slidesPerView={isMobile ? 1.2 : 4}
            navigation={!isMobile}
            pagination={{
              clickable: true,
              type: "progressbar",
            }}
            style={{
              paddingTop: "32px",
              paddingBottom: isMobile ? "60px" : "50px",
              paddingLeft: isMobile ? "32px" : "56px",
              paddingRight: isMobile ? "32px" : "56px",
            }}
          >
            {suggestions.map((p) => {
              const imgPath =
                (p.galleryImages && p.galleryImages[0]) ||
                (p.rotationImages && p.rotationImages[0]) ||
                "";
              const imgSrc = imgPath ? buildImageUrl(imgPath) : "";

              const subtitle = p.shortDescription || p.productType;
              const price = formatPrice(p.price);

              const handleClick = () => {
                navigate(`/product/${p.slug}`, {
                  state: { product: p },
                });
              };

              return (
                <SwiperSlide key={p.id}>
                  <ButtonBase
                    onClick={handleClick}
                    sx={{
                      textAlign: "center",
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    {imgSrc && (
                      <Box
                        sx={{
                          mt: 1,
                          width: "100%",
                          maxWidth: isMobile ? 460 : 260,
                          mx: "auto",
                          mb: 3,
                          borderRadius: 3,
                          overflow: "hidden",
                          aspectRatio: "1 / 1",
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          loading="lazy"
                          decoding="async"
                          src={imgSrc}
                          alt={p.altText || p.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontFamily: `"Bodoni Moda", serif`,
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {p.name}
                    </Typography>
                    {subtitle && (
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 260,
                          mx: "auto",
                        }}
                      >
                        {subtitle}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      }}
                    ></Typography>
                  </ButtonBase>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {/* Custom Swiper styles  */}
          <style>
            {`
              /* Black arrows */
              .swiper-button-prev, .swiper-button-next {
                color: black !important;
                width: 24px !important;
                height: 24px !important;
              }

              /* Progress bar */
              .swiper-pagination-progressbar {
                background: rgba(0,0,0,0.1) !important;
                height: 2px !important;
                width: 50% !important;
                margin: 0 auto;
                left: 25% !important;
                bottom: 10px !important;
              }

              .swiper-pagination-progressbar-fill {
                background: black !important;
              }

              /* Mobile shrink arrows + center pagination */
              @media (max-width: 900px) {
                .swiper-button-prev, .swiper-button-next {
                  width: 18px !important;
                  height: 18px !important;
                  top: auto !important;
                  bottom: 20px !important;
                }

                .swiper-button-prev { left: 40%; }
                .swiper-button-next { right: 40%; }
                .swiper-pagination-progressbar {
                  bottom: 20px !important;
                  width: 30% !important;
                  left: 35% !important;
                }
              }
            `}
          </style>
        </>
      )}
    </Box>
  );
};

export default YouMayAlsoLike;
